import {
  APPRAISAL_OPEX_DEFAULTS,
  INITIAL_COMMERCIAL_UNITS,
  INITIAL_HMO_ROOMS,
  commercialUnitOpexDefault,
  isCoWorkStrategy,
  isServicedAccommodationStrategy,
} from '@/components/deals/appraisal/appraisal-data'

const numberFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 })
const RESIDENTIAL_PROPERTY_TYPES = new Set([
  '1-Bed Flat',
  '2-Bed Flat',
  '3-Bed Flat',
  '2-Bed Terraced House',
  '3-Bed Terraced House',
  '2-Bed Semi-Detached House',
  '3-Bed Semi-Detached House',
  '4-Bed Detached House',
  'Flat',
  'Terraced House',
  'Semi-Detached House',
  'Detached House',
])

const DEFAULT_BRRR_STATE = {
  exitMode: 'Hold',
  propertyType: 'Flat',
  bedroomCount: '',
  valuationMethod: 'Comparable',
  suggestedGdv: '',
  adoptedGdv: '',
  suggestedSqft: '',
  adoptedSqft: '',
  internalArea: '',
  operatingModel: 'AST',
  suggestedMonthlyRent: '',
  adoptedMonthlyRent: '',
  astOpexMode: 'percentage',
  astOpexPercent: APPRAISAL_OPEX_DEFAULTS.leasedOrAst,
  astAnnualOperatingCost: '',
  suggestedAdr: '',
  adoptedAdr: '',
  occupancy: '75',
  opexPercent: APPRAISAL_OPEX_DEFAULTS.servicedAccommodation,
}

const DEFAULT_HMO_STATE = {
  rooms: INITIAL_HMO_ROOMS,
  valuationMethod: 'HMO Comparable',
  propertyType: 'Flat',
  constructionDate: 'Pre 1914',
  finishQuality: 'Very High',
  outdoorSpace: 'None',
  hasLivingRoom: true,
  hasParking: false,
  hmoSuggestedValue: '',
  hmoAdoptedValue: '',
  suggestedSqft: '',
  adoptedSqft: '',
  internalArea: '',
  adoptedEndValue: '',
  opexPercent: APPRAISAL_OPEX_DEFAULTS.hmo,
  yieldPercent: '8',
}

const SUGGESTION_FIELDS = {
  adoptedGdv: 'suggestedGdv',
  adoptedSqft: 'suggestedSqft',
  adoptedMonthlyRent: 'suggestedMonthlyRent',
  adoptedRent: 'suggestedRent',
  adoptedValue: 'suggestedValue',
  adoptedSqftValue: 'suggestedSqftValue',
  adoptedSqftYear: 'suggestedSqftYear',
}

export function buildMarketAwareAppraisalStates({ currentInputs, enrichment, selectedStrategy, evidence } = {}) {
  const scheme = currentInputs?.scheme ?? {}
  const brrr = scheme.brrr ?? {}
  const hmo = scheme.hmo ?? {}
  const commercial = scheme.commercial ?? {}
  const isCoWork = isCoWorkStrategy(selectedStrategy)
  const sourceUnit = enrichment?.units?.find(isResidentialUnit)
  const propertyType = brrr.propertyType ?? appraisalUseType(sourceUnit) ?? enrichment?.enrichment?.propertyType ?? 'Flat'
  const internalArea = brrr.internalArea ?? sourceUnit?.area ?? enrichment?.enrichment?.approxSizeSqFt ?? ''
  const states = {
    brrr: clearSuggestions({
      ...DEFAULT_BRRR_STATE,
      operatingModel: isServicedAccommodationStrategy(selectedStrategy) ? 'SA' : 'AST',
      internalArea: marketValue(internalArea),
      ...brrr,
      propertyType,
      bedroomCount: String(brrr.bedroomCount ?? enrichment?.enrichment?.bedroomCount ?? sourceUnit?.bedroomCount ??
        sourceUnit?.type?.match(/(\d+)[- ]bed/i)?.[1] ?? ''),
    }),
    hmo: {
      ...clearSuggestions({ ...DEFAULT_HMO_STATE, ...hmo }),
      rooms: (hmo.rooms ?? INITIAL_HMO_ROOMS).map((room) => clearSuggestions({ ...room })),
    },
    commercial: {
      units: Array.isArray(commercial.units)
        ? commercial.units.map((unit) => clearSuggestions({
          ...unit,
          opexPercent: unit.opexPercent ?? commercialUnitOpexDefault({ ...unit, isCoWork }),
        }))
        : incomeScheduleUnits(enrichment?.units, isCoWork),
    },
  }
  return projectSuggestions(states, evidence)
}

export function projectSuggestions(states, evidence) {
  const result = structuredClone(states)
  for (const suggestion of evidence?.suggestions ?? []) {
    const target = suggestion.target
    const field = SUGGESTION_FIELDS[target?.field]
    if (!field) continue
    const group = result[target.scheme]
    if (!group) continue
    const rowId = target.rowId ?? target.row_id
    const row = rowId == null ? group :
      (group.rooms ?? group.units ?? []).find((item) => String(item.id) === String(rowId))
    if (row) row[field] = marketValue(suggestion.value)
  }
  return result
}

export function applyEvidenceChanges(states, changes) {
  const result = structuredClone(states)
  for (const change of changes ?? []) {
    const target = change.target
    if (!target?.scheme || !target?.field) continue
    const rowId = target.rowId ?? target.row_id
    const group = result[target.scheme] ?? (rowId == null ? (result[target.scheme] = {}) : null)
    if (!group) continue
    const row = rowId == null ? group :
      (group.rooms ?? group.units ?? []).find((item) => String(item.id) === String(rowId))
    if (row) row[target.field] = String(change.value ?? '')
  }
  return result
}

export function blankEvidenceChanges(states, evidence) {
  return (evidence?.suggestions ?? []).flatMap((suggestion) => {
    if (!isBlank(targetValue(states, suggestion.target))) return []

    return [{
      target: suggestion.target,
      suggestionId: suggestion.id,
      value: String(suggestion.value ?? ''),
      unit: suggestion.unit,
    }]
  })
}

export function changedEvidenceByTarget(states, evidence) {
  const provenance = evidence?.adoptedBasis?.provenance ?? {}

  return Object.fromEntries((evidence?.suggestions ?? []).flatMap((suggestion) => {
    const current = targetValue(states, suggestion.target)
    if (isBlank(current) || normalizedValue(current) === normalizedValue(suggestion.value)) return []

    const path = inputTargetPath(suggestion.target)
    const source = provenance[path] ?? {}
    if ([source.suggestion_id, source.reviewed_suggestion_id].includes(suggestion.id)) return []

    return [[path, {
      ...suggestion,
      suggestionId: suggestion.id,
      adoptedValue: current,
      suggestedValue: suggestion.value,
    }]]
  }))
}

export function inputTargetPath(target) {
  const rowId = target?.rowId ?? target?.row_id
  const rowPath = rowId == null
    ? ''
    : `.${target.scheme === 'hmo' ? 'rooms' : 'units'}[${rowId}]`
  return `scheme.${target?.scheme}${rowPath}.${target?.field}`
}

export function hasManualEvidenceOverrides(states, savedInputs, evidence) {
  const savedStates = savedInputs?.scheme ?? {}
  return (evidence?.suggestions ?? []).some((suggestion) => {
    const current = targetValue(states, suggestion.target)
    if (isBlank(current)) return false

    return normalizedValue(current) !== normalizedValue(targetValue(savedStates, suggestion.target))
  })
}

export function buildCommercialSchemeFromIncomeSchedule({ enrichment, selectedStrategy, currentState } = {}) {
  const source = incomeScheduleUnits(enrichment?.units, isCoWorkStrategy(selectedStrategy))
  const current = currentState?.units ?? []
  const bySource = new Map(current.map((unit) => [String(unit.sourceUnitId ?? unit.id), unit]))
  const merged = source.map((unit) => {
    const previous = bySource.get(String(unit.sourceUnitId ?? unit.id))
    // Refresh source descriptors, never previously adopted assumptions or physical scheme edits.
    return previous ? { ...unit, ...previous, sourceUpdatedAt: unit.sourceUpdatedAt } : unit
  })
  const included = new Set(merged.map((unit) => String(unit.id)))
  return { units: [...merged, ...current.filter((unit) => !included.has(String(unit.id)))] }
}

export function commercialSchemeMatchesIncomeSchedule(appraisalUnits, incomeUnits) {
  const current = appraisalUnits ?? []
  const sources = incomeUnits ?? []
  const linked = current.filter((unit) => unit.sourceUnitId)
  return linked.length === sources.length && sources.every((source) => linked.some((unit) =>
    String(unit.sourceUnitId) === String(source.id) &&
    String(unit.sourceUpdatedAt ?? '') === String(source.updatedAt ?? '')))
}

export function refreshBrrrMarketData(previous, next) {
  const changed = ['propertyType', 'bedroomCount', 'internalArea', 'operatingModel']
    .some((field) => previous[field] !== next[field])
  return changed ? clearSuggestions(next) : next
}

export function refreshHmoMarketData(previous, next) {
  const old = new Map((previous.rooms ?? []).map((row) => [String(row.id), row]))
  return { ...next, rooms: (next.rooms ?? []).map((row) =>
    ['type', 'bathroom', 'occupancy', 'billsIncluded'].every((field) => old.get(String(row.id))?.[field] === row[field])
      ? row : clearSuggestions(row)) }
}

export function refreshCommercialMarketData(previous, next) {
  const old = new Map((previous.units ?? []).map((row) => [String(row.id), row]))
  return { ...next, units: (next.units ?? []).map((row) =>
    ['useType', 'sizeSqft', 'operatingModel', 'sourceUnitId'].every((field) => old.get(String(row.id))?.[field] === row[field])
      ? row : clearSuggestions(row)) }
}

// Compatibility readers never accept assumptions; adoption uses the evidence review endpoint.
export function applyBrrrSubjectComparable(current) { return clearSuggestions(current) }
export function appraisalSubjectComparable() { return null }

function clearSuggestions(state) {
  return Object.fromEntries(Object.entries(state).map(([key, value]) => [
    key, /^(suggested|hmoSuggested)/.test(key) ? '' : value,
  ]))
}

function incomeScheduleUnits(units, isCoWork) {
  if (!Array.isArray(units) || units.length === 0) {
    return INITIAL_COMMERCIAL_UNITS.map((unit) => clearSuggestions({ ...unit,
      opexPercent: commercialUnitOpexDefault({ ...unit, isCoWork }),
    }))
  }
  return units.map((unit, index) => {
    const useType = appraisalUseType(unit)
    const residential = /flat|house|studio|apartment/i.test(useType)
    return {
      id: `income-${unit.id ?? index + 1}`, sourceUnitId: unit.id ?? null,
      sourceUpdatedAt: unit.updatedAt ?? null, sourceStatus: unit.status ?? null,
      currentMonthlyRent: unit.rent ?? null, currentAnnualRent: unit.annualRent ?? null,
      name: unit.unit || `Unit ${index + 1}`, useType, sizeSqft: marketValue(unit.area),
      suggestedSqftYear: '', adoptedSqftYear: '',
      opexPercent: commercialUnitOpexDefault({ useType, operatingModel: 'AST', isCoWork }),
      adoptedYield: residential ? '7' : '8.5', suggestedMonthlyRent: '', adoptedMonthlyRent: '',
      suggestedAdr: '', adoptedAdr: '', occupancy: residential ? '80' : '75',
      suggestedValue: '', adoptedValue: '', suggestedSqftValue: '', adoptedSqftValue: '',
      valuationBasis: residential ? 'Comparable' : 'Commercial', operatingModel: 'AST',
    }
  })
}

function appraisalUseType(unit) {
  if (!unit) return null

  const rawType = typeof unit === 'object' ? unit.type : unit
  const physicalType = typeof unit === 'object' ? String(unit.physicalPropertyType ?? '').trim() : ''
  const bedrooms = typeof unit === 'object'
    ? unit.bedroomCount ?? String(rawType ?? '').match(/(\d+)[- ]bed/i)?.[1]
    : null
  const residentialType = physicalType && bedrooms ? `${bedrooms}-Bed ${physicalType}` : physicalType
  if (RESIDENTIAL_PROPERTY_TYPES.has(residentialType)) return residentialType
  if (RESIDENTIAL_PROPERTY_TYPES.has(physicalType)) return physicalType

  const value = String(rawType ?? '').trim()
  if (/^offices?$/i.test(value)) return 'Office'
  if (/^(industrial|warehouse)$/i.test(value)) return 'Industrial / Warehouse'
  if (!value || /^commercial$/i.test(value)) return 'Other Commercial'
  return value
}

function isResidentialUnit(unit) {
  return /flat|house|studio|apartment/i.test([unit?.physicalPropertyType, unit?.type].filter(Boolean).join(' '))
}

function marketValue(value) {
  if (value === null || value === undefined || value === '') return ''
  const number = Number(String(value).replaceAll(',', ''))
  return Number.isFinite(number)
    ? numberFormatter.format(number)
    : ''
}

function targetValue(states, target) {
  const group = states?.[target?.scheme]
  if (!group || !target?.field) return undefined
  const rowId = target.rowId ?? target.row_id
  const row = rowId == null ? group :
    (group.rooms ?? group.units ?? []).find((item) => String(item.id) === String(rowId))
  return row?.[target.field]
}

function isBlank(value) {
  return String(value ?? '').trim() === ''
}

function normalizedValue(value) {
  const numeric = Number(String(value ?? '').replaceAll(',', ''))
  return Number.isFinite(numeric) ? String(numeric) : String(value ?? '').trim()
}
